import type { OutcomeSummary } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { computeDependencyLayers, getBlockedDependencies } from '@/lib/outcome-dependencies';

interface DependencyGraphCardProps {
  outcomes: OutcomeSummary[];
  onView: (outcomeId: string) => void;
}

export function DependencyGraphCard({
  outcomes,
  onView,
}: DependencyGraphCardProps): React.ReactElement | null {
  const layers = computeDependencyLayers(outcomes);

  if (layers.length === 0 || outcomes.every(outcome => (outcome.dependsOn?.length ?? 0) === 0)) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dependency Graph</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {layers.map((layer, layerIndex) => (
          <div key={`layer-${String(layerIndex)}`} className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
              Layer {layerIndex + 1}
            </div>
            <div className="flex flex-wrap gap-2">
              {layer.map(outcome => {
                const blockedBy = getBlockedDependencies(outcome, outcomes);
                return (
                  <button
                    key={outcome.id}
                    type="button"
                    onClick={(): void => {
                      onView(outcome.id);
                    }}
                    className="min-w-[13rem] rounded-lg border border-border bg-surface-elevated p-3 text-left transition-colors hover:bg-surface-elevated/80"
                    title={
                      blockedBy.length > 0
                        ? `Blocked by: ${blockedBy.join(', ')}`
                        : outcome.dependsOn?.length
                          ? `Depends on: ${outcome.dependsOn.join(', ')}`
                          : 'No dependencies'
                    }
                  >
                    <div className="font-mono text-xs text-text-secondary">{outcome.id}</div>
                    <div className="mt-1 text-sm font-medium text-text-primary">
                      {outcome.title}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Badge variant={outcome.status === 'complete' ? 'default' : 'secondary'}>
                        {outcome.status}
                      </Badge>
                      {(outcome.dependsOn?.length ?? 0) > 0 && (
                        <span className="text-[11px] text-text-secondary">
                          {outcome.dependsOn?.length} dep
                          {(outcome.dependsOn?.length ?? 0) > 1 ? 's' : ''}
                        </span>
                      )}
                      {blockedBy.length > 0 && (
                        <span className="text-[11px] font-medium text-warning">
                          blocked by {blockedBy.join(', ')}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
